// The tense check (`Tense`, SuiteStyle Tense.swift): a scene's narration read for tense markers — past ("was", "went",
// "she looked") and present ("is", "goes", "she looks") — outside dialogue. Once a scene clearly tells in one tense, a
// sentence whose only markers are the other tense is a candidate; once most of the book clearly tells in one tense, a
// scene told in the other gets one note. Word lists and rules, the same as the Swift's, so the answers match; no
// tagger, so the check is exact on the web. Backstory in a present-tense scene is left alone when the sentence says
// when ("yesterday", "ago", "had"); a candidate is only ever a question.
import { sentenceRanges } from '../../text/sentences.ts';
import type { Range16 } from '../../text/scalars.ts';

export type TenseWhich = 'past' | 'present';

const words = (s: string) => new Set(s.split(/\s+/).filter(Boolean));

const IRREGULAR_PAST = words(`was were had did went came said saw took made got gave knew thought felt told became began found left ran sat stood
  heard held kept brought bought caught taught fought sought fell met led read spoke broke wrote drove rode rose chose
  froze stole woke threw grew knew drew flew blew slept wept swept kept crept lost built sent spent lent bent meant
  learnt dreamt burnt leant knelt dealt shook forgot understood withdrew wore tore swore bore sang rang drank sank
  shrank sprang swam ran began hung struck stuck dug swung clung flung slung strung wrung won spun lay laid paid
  hid bit lit slid fed bled bred led sped shot put cut hit let set shut quit split spread hurt cost`);
const PRESENT_THIRD = words(`says looks walks turns takes goes comes sees knows thinks feels wants tells asks runs sits stands opens closes
  pulls pushes nods smiles laughs watches waits moves holds puts gets gives makes finds keeps leaves lets begins
  starts stops tries seems hears reaches steps shakes follows calls picks drops lifts falls pours leans waves grabs
  stares glances whispers shrugs sighs breathes remembers wonders realises realizes decides needs means happens
  becomes remains lies dies lives works plays reads writes sleeps wakes dreams cries screams shouts drives rides
  flies swims climbs crosses enters passes carries brings sends meets kisses touches hits throws catches kicks cuts
  builds breaks burns blows grows draws drinks eats cooks buys sells pays wears hangs hides counts points raises
  rises crouches kneels slips trips sinks swings flings clutches presses squeezes rubs wipes blinks frowns grins`);
const PRESENT_BASE = words(`walk turn take go come see know think feel want tell ask run sit stand open close pull push nod smile laugh look
  watch wait move hold put get give make find keep leave let begin start stop try hear reach step shake follow call
  pick drop lift fall lean stare glance whisper shrug sigh breathe remember wonder decide need mean say lie live
  work read write sleep wake cry shout drive climb cross enter pass carry bring send meet touch throw catch cut
  build break blow grow draw drink eat buy pay wear hang hide count point raise rise crouch kneel slip trip sink
  swing fling clutch press squeeze rub wipe blink frown grin`);
const NOT_PAST_ED = words('need indeed bed red shed sled wed fed bred bled sped seed weed feed deed reed speed bleed breed greed creed tweed');
const AUXILIARIES = words(`to will would could should might may must can shall is are am was were be been being has have had having get got gets getting
  do does did don’t don't doesn’t doesn't didn’t didn't ’ll 'll ’d 'd ’s 's not never`);
const SUBJECTS = words('i he she it we they you who');
const PRESENT_FINITE = words("is are am has have does do isn’t isn't aren’t aren't doesn’t doesn't hasn’t hasn't haven’t haven't");
const BACKSTORY = words('yesterday ago last once before earlier previously formerly then had used childhood younger years months weeks remember remembered remembers');

export interface TenseMarker { range: Range16; word: string; tense: TenseWhich }

const isName = (w: string) => w.length >= 2 && /^[A-Z][a-z’']*$/.test(w);
const isSubject = (w: string) => SUBJECTS.has(w) || isName(w);

/** Every marker in a paragraph's narration (dialogue left out), with a one-word look behind. */
export function tenseMarkers(text: string, quoted: readonly Range16[]): TenseMarker[] {
  const found: { word: string; range: Range16 }[] = [];
  for (const m of text.matchAll(/[A-Za-z’']+/g)) {
    const range: Range16 = [m.index!, m.index! + m[0].length];
    if (quoted.some((q) => q[0] < range[1] && range[0] < q[1])) continue;
    found.push({ word: m[0], range });
  }
  const out: TenseMarker[] = [];
  found.forEach((item, i) => {
    const lower = item.word.toLowerCase();
    const before = i > 0 ? found[i - 1].word.toLowerCase() : '';
    const twoBefore = i > 1 ? found[i - 2].word.toLowerCase() : '';
    if (AUXILIARIES.has(before)) return;
    if (IRREGULAR_PAST.has(lower)) { out.push({ range: item.range, word: item.word, tense: 'past' }); return; }
    if (lower.endsWith('ed') && lower.length >= 4 && !NOT_PAST_ED.has(lower) && (isSubject(before) || (before.endsWith('ly') && isSubject(twoBefore)))) {
      out.push({ range: item.range, word: item.word, tense: 'past' }); return;
    }
    if (PRESENT_FINITE.has(lower)) { out.push({ range: item.range, word: item.word, tense: 'present' }); return; }
    if (PRESENT_THIRD.has(lower) && (['he', 'she', 'it', 'and', 'then', 'but'].includes(before) || (i > 0 && isName(found[i - 1].word)))) {
      out.push({ range: item.range, word: item.word, tense: 'present' }); return;
    }
    if (PRESENT_BASE.has(lower) && (before === 'i' || before === 'we' || before === 'you' || before === 'they')) {
      out.push({ range: item.range, word: item.word, tense: 'present' });
    }
  });
  return out;
}

export interface TenseSentence { tense: TenseWhich; marker: TenseMarker; paragraph: number; dated: boolean }

export interface TenseScene {
  past: number;
  present: number;
  sentences: TenseSentence[];
}

/** The tense the scene is told in: three to one and at least five markers, else null. */
export function sceneTold(scene: TenseScene): TenseWhich | null {
  if (scene.past >= 5 && scene.past >= scene.present * 3) return 'past';
  if (scene.present >= 5 && scene.present >= scene.past * 3) return 'present';
  return null;
}

/** A scene's tally and the sentences that go one way, over its paragraphs (`quoted` gives each one's dialogue). */
export function scanTense<P extends { text: string }>(paragraphs: readonly P[], quoted: (p: P) => readonly Range16[]): TenseScene {
  const scene: TenseScene = { past: 0, present: 0, sentences: [] };
  paragraphs.forEach((p, index) => {
    if (p.text.startsWith('>') || p.text.startsWith('#')) return;
    const markers = tenseMarkers(p.text, quoted(p));
    if (!markers.length) return;
    for (const sentence of sentenceRanges(p.text)) {
      const inside = markers.filter((m) => m.range[0] >= sentence[0] && m.range[0] < sentence[1]);
      if (!inside.length) continue;
      const first = inside[0];
      scene.past += inside.filter((m) => m.tense === 'past').length;
      scene.present += inside.filter((m) => m.tense === 'present').length;
      if (!inside.every((m) => m.tense === first.tense)) continue;
      const sentenceWords = p.text.slice(sentence[0], sentence[1]).toLowerCase().split(/[^a-z’]+/).filter(Boolean);
      const dated = sentenceWords.some((w) => BACKSTORY.has(w) || /^(1[5-9]|20)\d\d$/.test(w));
      scene.sentences.push({ tense: first.tense, marker: first, paragraph: index, dated });
    }
  });
  return scene;
}

export interface TenseNote { paragraph: number; range: Range16; message: string }

/** Findings for every scene: sentences against the scene's tense (at most `limit` a scene), and a note on a scene told
 *  against most of the book (once at least three scenes have a tense). One list per scene, in the scenes' order. */
export function tenseFindings(scenes: readonly TenseScene[], limit = 12): TenseNote[][] {
  const decided = scenes.map(sceneTold).filter((t): t is TenseWhich => t !== null);
  let bookTense: TenseWhich | null = null;
  if (decided.length >= 3) {
    const past = decided.filter((t) => t === 'past').length, present = decided.length - past;
    if (past >= present * 3) bookTense = 'past';
    else if (present >= past * 3) bookTense = 'present';
  }
  return scenes.map((scene) => {
    const notes: TenseNote[] = [];
    const told = sceneTold(scene);
    if (!told) return notes;
    const against = scene.sentences.filter((s) => s.tense !== told && !(told === 'present' && s.dated));
    for (const s of against.slice(0, limit)) {
      notes.push({
        paragraph: s.paragraph, range: s.marker.range,
        message: told === 'past'
          ? `Present tense in a scene told in the past: “${s.marker.word}”. A slip, or a deliberate aside?`
          : `Past tense in a scene told in the present: “${s.marker.word}”. A slip, or a step back in time?`,
      });
    }
    if (bookTense && told !== bookTense) {
      const first = scene.sentences.find((s) => s.tense === told);
      if (first) notes.push({ paragraph: first.paragraph, range: first.marker.range, message: `This scene is told in the ${told} tense; most of the manuscript is in the ${bookTense}.` });
    }
    return notes;
  });
}
